import { motion } from 'framer-motion';
import { BookOpen, Landmark, ListChecks, CalendarDays, Lightbulb } from 'lucide-react';
import { templeGuides } from '@/data/templeGuides';

export const TempleDetailedGuide = ({ temple }) => {
  const guide = templeGuides[temple.id];
  if (!guide) return null;

  return (
    <section
      className="py-16 md:py-24 bg-background border-t border-orange-100/60"
      data-testid="temple-detailed-guide"
    >
      <div className="container mx-auto px-6 md:px-12 max-w-5xl">
        <div className="mb-10">
          <div className="inline-block px-4 py-1 bg-saffron-100 text-saffron-700 font-outfit text-xs uppercase tracking-wider mb-4">
            Pilgrim's Detailed Guide
          </div>
          <h2 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-temple-slate mb-3">
            The Complete Guide to {temple.name} Jyotirlinga
          </h2>
          <p className="font-outfit text-base text-gray-600 max-w-3xl">
            Everything a pilgrim needs — a millennium of history, the specifics of the sanctum's
            architecture, the exact darshan procedure, the annual festival calendar, and
            practical tips born from experience on the ground.
          </p>
        </div>

        {/* Detailed History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10"
          data-testid="detailed-history"
        >
          <div className="flex items-center mb-4">
            <BookOpen className="w-6 h-6 text-saffron-600 mr-3" />
            <h3 className="font-cormorant text-2xl md:text-3xl font-bold text-temple-slate">
              A Thousand Years of History
            </h3>
          </div>
          <p className="font-outfit text-base text-gray-700 leading-relaxed">
            {guide.detailedHistory}
          </p>
        </motion.div>

        {/* Architecture */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10"
          data-testid="temple-architecture"
        >
          <div className="flex items-center mb-4">
            <Landmark className="w-6 h-6 text-saffron-600 mr-3" />
            <h3 className="font-cormorant text-2xl md:text-3xl font-bold text-temple-slate">
              Architecture &amp; Design of the Sanctum
            </h3>
          </div>
          <p className="font-outfit text-base text-gray-700 leading-relaxed">
            {guide.architecture}
          </p>
        </motion.div>

        {/* Step-by-step Darshan Procedure */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10 bg-white border border-orange-100/60 p-6 md:p-8"
          data-testid="darshan-procedure"
        >
          <div className="flex items-center mb-5">
            <ListChecks className="w-6 h-6 text-saffron-600 mr-3" />
            <h3 className="font-cormorant text-2xl md:text-3xl font-bold text-temple-slate">
              Step-by-Step Darshan Procedure
            </h3>
          </div>
          <ol className="space-y-3">
            {guide.darshanProcedure.map((step, idx) => (
              <li key={idx} className="flex items-start" data-testid={`darshan-step-${idx}`}>
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-saffron-500 text-white text-sm font-outfit font-semibold flex items-center justify-center mr-3 mt-0.5">
                  {idx + 1}
                </span>
                <span className="font-outfit text-gray-700 leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
        </motion.div>

        {/* Festival Calendar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10"
          data-testid="festival-calendar"
        >
          <div className="flex items-center mb-5">
            <CalendarDays className="w-6 h-6 text-saffron-600 mr-3" />
            <h3 className="font-cormorant text-2xl md:text-3xl font-bold text-temple-slate">
              Annual Festival Calendar
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {guide.festivals.map((festival, idx) => (
              <div
                key={idx}
                className="bg-white border border-orange-100/60 p-5"
                data-testid={`festival-${idx}`}
              >
                <h4 className="font-cormorant text-xl font-bold text-temple-slate mb-2">
                  {festival.name}
                </h4>
                <p className="font-outfit text-sm text-gray-700 leading-relaxed">
                  {festival.description}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Pilgrim Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-saffron-50 border-l-4 border-saffron-500 p-6 md:p-8"
          data-testid="pilgrim-tips"
        >
          <div className="flex items-center mb-4">
            <Lightbulb className="w-6 h-6 text-saffron-600 mr-3" />
            <h3 className="font-cormorant text-2xl md:text-3xl font-bold text-temple-slate">
              Insider Tips for Pilgrims
            </h3>
          </div>
          <ul className="space-y-2">
            {guide.pilgrimTips.map((tip, idx) => (
              <li key={idx} className="flex items-start" data-testid={`pilgrim-tip-${idx}`}>
                <span className="w-1.5 h-1.5 bg-saffron-500 rounded-full mt-2.5 mr-3 flex-shrink-0"></span>
                <span className="font-outfit text-gray-700 leading-relaxed">{tip}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
};
